/**
 * Subcontractor Settings Page — /office/settings/subcontractors/
 *
 * Wires the create/edit dynamic modal for ``SubcontractorCompany``,
 * following the canonical form-modal pattern documented in
 * ``.cursor/rules/form-modals.mdc`` (and mirrored by
 * ``worksite-detail.js``).
 *
 * Triggers (delegated on ``document.body``):
 *   - ``[data-subcontractor-modal-open][data-subcontractor-modal-mode="create"]``
 *     — header "Add subcontractor" button.
 *   - ``[data-subcontractor-modal-open][data-subcontractor-modal-mode="edit"]``
 *     — per-row "Edit" button in ``_table.html``. URL is read from
 *     ``data-subcontractor-edit-url``.
 *
 * Template contract on the root container:
 *   ``[data-auto-init="subcontractorSettings"
 *      data-subcontractor-create-url="..."
 *      data-subcontractor-modal-create-title="..."
 *      data-subcontractor-modal-edit-title="..."]``
 *
 * Lifecycle:
 *   - The modal helper is created at ``initSubcontractorSettings()`` time
 *     and bound to the static ``#subcontractor-modal`` shell.
 *   - On ``htmx:afterSettle`` (boosted nav or after a successful soft-
 *     reload), the modal shell is replaced by a fresh DOM node with the
 *     same id; the cached helper now points at an orphan, so we null it
 *     out and re-init. See § 9 of ``form-modals.mdc``.
 *
 * The deactivate / reactivate toggle in ``_table.html`` is **not**
 * routed through this module — it is a plain HTMX swap that targets
 * ``#subcontractor-table-mount`` (no form modal involved).
 */

import { createDynamicModal } from '../utils/ui/dynamic-modal.js';
import { initAllPhoneInputs } from '../utils/forms/phone-input.js';

let globalBound = false;
let modalHelper = null;

/**
 * Wire the registration_mode → "signed contract reference" agreement
 * block toggle inside the freshly-injected modal body. Called from
 * ``onContentInjected`` so it runs on the initial GET and on every
 * 422 re-render. Idempotent and silent when the form doesn't carry
 * the toggle (e.g. the registration_mode field is removed by feature
 * flag).
 *
 * Mirrors the server-side rule: the agreement reference is only
 * meaningful — and required — when the office picks ``USES_OUR_SYSTEM``.
 * Defence in depth lives in ``SubcontractorCompanyForm.clean()`` and
 * the DB ``CheckConstraint``; this is purely UX so the field doesn't
 * stay populated with a stale value when the user switches modes.
 */
function _initAgreementToggle(contentEl) {
    if (!contentEl) return;
    var modeSelect = contentEl.querySelector('[data-registration-mode-toggle]');
    var agreementBlock = contentEl.querySelector('[data-subcontractor-agreement]');
    if (!modeSelect || !agreementBlock) return;

    function sync() {
        var isOurs = modeSelect.value === 'uses_ours';
        agreementBlock.classList.toggle('hidden', !isOurs);
        if (!isOurs) {
            var input = agreementBlock.querySelector('input, textarea');
            if (input) input.value = '';
        }
    }
    modeSelect.addEventListener('change', sync);
    sync();
}

function initModal() {
    if (modalHelper) return;
    modalHelper = createDynamicModal({
        modalId: 'subcontractor-modal',
        onSuccess: function (data, helper) {
            // Toast the server-authoritative success message (i18n done
            // server-side, shipped in the JSON), then close + soft-reload
            // the table. Closing with a deferred callback avoids the
            // "white flash" race (see dynamic-modal close() docs).
            if (data && data.message && typeof window.showToast === 'function') {
                window.showToast(data.message, 'success');
            }
            helper.close(function () {
                if (typeof window._softReload === 'function') window._softReload();
            });
        },
        onContentInjected: function (contentEl) {
            // ``form-select`` opt-in for the custom-select wrapper —
            // see ``form-modals.mdc`` §5 + ``frontend/src/custom-select.js``.
            // Must run on every inject (initial GET + 422 re-render),
            // since the helper replaces ``.modal-content`` innerHTML.
            if (typeof window.initCustomSelects === 'function') {
                window.initCustomSelects();
            }
            // Wrap any ``[data-phone-input]`` field with intl-tel-input —
            // same helper as the team form / company settings page. Scoped
            // to the modal so we don't disturb phone inputs already
            // initialised on the host page (defence in depth — there are
            // none on the subcontractor list, but the convention applies
            // everywhere).
            initAllPhoneInputs(contentEl);
            // Phase 21.C — registration_mode → agreement-block toggle
            // (replaces the per-modal inline ``<script>`` that used to
            // ship with ``_modal_form.html``).
            _initAgreementToggle(contentEl);
            if (typeof window.reinitFlowbiteWidgets === 'function') {
                var modalEl = document.getElementById('subcontractor-modal');
                if (modalEl) window.reinitFlowbiteWidgets(modalEl);
            }
        },
    });
}

function getRoot() {
    return document.querySelector('[data-auto-init="subcontractorSettings"]');
}

function handleDelegatedClick(evt) {
    var t = evt.target;
    if (!t || !t.closest) return;

    var btn = t.closest('[data-subcontractor-modal-open]');
    if (!btn) return;

    var root = getRoot();
    if (!root) return;
    if (!modalHelper) return;

    evt.preventDefault();

    var mode = btn.dataset.subcontractorModalMode || 'create';
    var url;
    var title;

    if (mode === 'edit') {
        url = btn.dataset.subcontractorEditUrl;
        title = root.dataset.subcontractorModalEditTitle || 'Edit subcontractor';
    } else {
        url = root.dataset.subcontractorCreateUrl;
        title = root.dataset.subcontractorModalCreateTitle || 'Add subcontractor';
    }

    if (!url) return;
    modalHelper.open(url, title);
}

function handleAfterSettle(evt) {
    var target = evt.detail && evt.detail.target;
    if (!target || target.id !== 'main-content') return;
    modalHelper = null;
    initModal();
}

function bindGlobalDelegation() {
    if (globalBound) return;
    globalBound = true;
    document.body.addEventListener('click', handleDelegatedClick);
    document.body.addEventListener('htmx:afterSettle', handleAfterSettle);
}

export function initSubcontractorSettings() {
    modalHelper = null;
    bindGlobalDelegation();
    initModal();
}

export function autoInitSubcontractorSettings() {
    if (!getRoot()) return;
    initSubcontractorSettings();
}
